import React, { useState, useEffect } from 'react';
import { BomberosHeader } from '../../components/BomberosHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    bomberosCapacitacionAPI,
    bomberosCompetenciaAPI,
    bomberosCompaniaAPI,
    bomberosMallaAPI,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Save,
    GraduationCap,
    Calendar,
    Clock,
    MapPin,
    Users,
    BookOpen,
    Building2,
} from 'lucide-react';

// Módulos de formación basados en documentación ANB
const MODULOS_FORMACION = [
    { value: 'formacion_aspirantes', label: 'Formación de Aspirantes' },
    { value: 'entrenamiento_estandar_anb', label: 'Entrenamiento Estándar ANB' },
    { value: 'academias', label: 'Academias' },
    { value: 'cursos_especialidades', label: 'Cursos Especialidades' },
    { value: 'formacion_oficiales', label: 'Formación de Oficiales' },
    { value: 'guardias_compania', label: 'Guardias de Compañía' },
    { value: 'cursos_anb', label: 'Cursos ANB (Comandancia)' },
    { value: 'capacitacion_interna', label: 'Capacitación Interna' },
];

// Tipos de capacitación
const TIPOS_CAPACITACION = [
    { value: 'incendio_estructural', label: 'Incendio Estructural' },
    { value: 'rescate_vehicular', label: 'Rescate Vehicular' },
    { value: 'rescate_cuerdas', label: 'Rescate con Cuerdas' },
    { value: 'hazmat', label: 'Materiales Peligrosos (HAZMAT)' },
    { value: 'incendio_forestal', label: 'Incendio Forestal' },
    { value: 'soporte_vital', label: 'Soporte Vital Básico' },
    { value: 'uso_epp_era', label: 'Uso de EPP y ERA' },
    { value: 'ventilacion_tactica', label: 'Ventilación Táctica' },
    { value: 'busqueda_rescate', label: 'Búsqueda y Rescate' },
    { value: 'entrada_forzada', label: 'Entrada Forzada' },
    { value: 'sci', label: 'Sistema de Comando de Incidentes' },
    { value: 'mando_liderazgo', label: 'Mando y Liderazgo' },
    { value: 'rescate_agreste', label: 'Rescate Agreste' },
    { value: 'rescate_urbano', label: 'Rescate Urbano' },
    { value: 'incendio_vehicular', label: 'Incendio Vehicular' },
    { value: 'abastecimiento', label: 'Abastecimiento' },
    { value: 'rit', label: 'RIT (Equipo de Intervención Rápida)' },
    { value: 'investigacion_incendio', label: 'Investigación de Incendio' },
    { value: 'riesgo_electrico', label: 'Riesgo Eléctrico' },
    { value: 'otro', label: 'Otro' },
];

export default function CapacitacionFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const isEditing = !!id;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [competencias, setCompetencias] = useState([]);
    const [companias, setCompanias] = useState([]);
    const [cursosMalla, setCursosMalla] = useState([]);
    
    const [form, setForm] = useState({
        codigo: '',
        nombre: '',
        descripcion: '',
        compania_id: 'todas',
        competencia_id: 'none',
        nivel_anb: 'inicial',
        modulo_formacion: 'capacitacion_interna',
        tipo_capacitacion: 'otro',
        horas_duracion: 8,
        fecha_inicio: '',
        fecha_fin: '',
        instructor: '',
        lugar: '',
        cupo_maximo: 20,
        estado: 'programada',
        objetivos: '',
        contenidos: '',
        requisitos_previos: '',
        materiales_necesarios: '',
        malla_curricular: [],
        es_aula_virtual: false,
        es_complementario: false,
        curso_malla_id: 'none',
    });

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            
            // Cargar competencias y compañías
            const [compsRes, companiasRes, mallaRes] = await Promise.all([
                bomberosCompetenciaAPI.getAll(),
                bomberosCompaniaAPI.getAll(),
                bomberosMallaAPI.getCursosLista(),
            ]);
            setCompetencias(compsRes.data);
            setCompanias(companiasRes.data);
            setCursosMalla(mallaRes.data);
            
            // Si estamos editando, cargar la capacitación
            if (isEditing) {
                const capRes = await bomberosCapacitacionAPI.getById(id);
                const cap = capRes.data;
                setForm({
                    codigo: cap.codigo || '',
                    nombre: cap.nombre || '',
                    descripcion: cap.descripcion || '',
                    compania_id: cap.compania_id || 'todas',
                    competencia_id: cap.competencia_id || 'none',
                    nivel_anb: cap.nivel_anb || 'inicial',
                    modulo_formacion: cap.modulo_formacion || 'capacitacion_interna',
                    tipo_capacitacion: cap.tipo_capacitacion || 'otro',
                    horas_duracion: cap.horas_duracion || 8,
                    fecha_inicio: cap.fecha_inicio || '',
                    fecha_fin: cap.fecha_fin || '',
                    instructor: cap.instructor || '',
                    lugar: cap.lugar || '',
                    cupo_maximo: cap.cupo_maximo || 20,
                    estado: cap.estado || 'programada',
                    objetivos: cap.objetivos || '',
                    contenidos: cap.contenidos || '',
                    requisitos_previos: cap.requisitos_previos || '',
                    materiales_necesarios: cap.materiales_necesarios || '',
                    malla_curricular: cap.malla_curricular || [],
                    es_aula_virtual: cap.es_aula_virtual || false,
                    es_complementario: cap.es_complementario || false,
                    curso_malla_id: cap.curso_malla_id || 'none',
                });
            } else {
                // Generar código automático
                const year = new Date().getFullYear();
                const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
                setForm(prev => ({
                    ...prev,
                    codigo: `CAP-${year}-${randomNum}`,
                }));
            }
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validaciones
        if (!form.nombre.trim()) {
            toast.error('El nombre es requerido');
            return;
        }
        if (!form.fecha_inicio || !form.fecha_fin) {
            toast.error('Las fechas de inicio y fin son requeridas');
            return;
        }
        if (form.fecha_fin < form.fecha_inicio) {
            toast.error('La fecha de fin debe ser posterior a la de inicio');
            return;
        }
        if (form.horas_duracion < 1) {
            toast.error('La duración debe ser al menos 1 hora');
            return;
        }

        try {
            setSaving(true);
            
            const payload = {
                ...form,
                compania_id: form.compania_id === 'todas' ? null : form.compania_id,
                competencia_id: form.competencia_id === 'none' ? null : form.competencia_id,
                curso_malla_id: form.curso_malla_id === 'none' ? null : form.curso_malla_id,
                empresa_id: user.empresa_id,
            };

            if (isEditing) {
                await bomberosCapacitacionAPI.update(id, payload);
                toast.success('Capacitación actualizada correctamente');
            } else {
                await bomberosCapacitacionAPI.create(payload);
                toast.success('Capacitación creada correctamente');
            }
            
            navigate('/bomberos/capacitaciones');
        } catch (err) {
            console.error('Error saving:', err);
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/capacitaciones')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <GraduationCap className="h-6 w-6 text-purple-500" />
                        {isEditing ? 'Editar Capacitación' : 'Nueva Capacitación'}
                    </h1>
                    <p className="text-muted-foreground">
                        {isEditing ? 'Modificar datos de la capacitación' : 'Programar una nueva capacitación'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Información Básica */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <BookOpen className="h-5 w-5" />
                            Información Básica
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="codigo">Código</Label>
                                <Input
                                    id="codigo"
                                    value={form.codigo}
                                    onChange={(e) => handleChange('codigo', e.target.value)}
                                    placeholder="CAP-2026-001"
                                    data-testid="input-codigo"
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="nombre">Nombre de la Capacitación *</Label>
                                <Input
                                    id="nombre"
                                    value={form.nombre}
                                    onChange={(e) => handleChange('nombre', e.target.value)}
                                    placeholder="Ej: Incendio Estructural Nivel Básico"
                                    required
                                    data-testid="input-nombre"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="descripcion">Descripción</Label>
                            <Textarea
                                id="descripcion"
                                value={form.descripcion}
                                onChange={(e) => handleChange('descripcion', e.target.value)}
                                placeholder="Descripción general de la capacitación..."
                                rows={3}
                                data-testid="input-descripcion"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4" />
                                    Compañía Destinataria
                                </Label>
                                <Select
                                    value={form.compania_id}
                                    onValueChange={(v) => handleChange('compania_id', v)}
                                >
                                    <SelectTrigger data-testid="select-compania">
                                        <SelectValue placeholder="Seleccionar compañía" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="todas">Todas las Compañías</SelectItem>
                                        {companias.map(c => (
                                            <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    Solo se podrán inscribir bomberos de la compañía seleccionada
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label>Módulo de Formación</Label>
                                <Select
                                    value={form.modulo_formacion}
                                    onValueChange={(v) => handleChange('modulo_formacion', v)}
                                >
                                    <SelectTrigger data-testid="select-modulo">
                                        <SelectValue placeholder="Seleccionar módulo" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MODULOS_FORMACION.map(m => (
                                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Tipo de Capacitación</Label>
                                <Select
                                    value={form.tipo_capacitacion}
                                    onValueChange={(v) => handleChange('tipo_capacitacion', v)}
                                >
                                    <SelectTrigger data-testid="select-tipo">
                                        <SelectValue placeholder="Seleccionar tipo" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {TIPOS_CAPACITACION.map(t => (
                                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Nivel ANB</Label>
                                <Select
                                    value={form.nivel_anb}
                                    onValueChange={(v) => handleChange('nivel_anb', v)}
                                >
                                    <SelectTrigger data-testid="select-nivel">
                                        <SelectValue placeholder="Seleccionar nivel" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="postulante">Postulante</SelectItem>
                                        <SelectItem value="inicial">Inicial</SelectItem>
                                        <SelectItem value="operativo">Operativo</SelectItem>
                                        <SelectItem value="profesional">Profesional</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Competencia que Otorga</Label>
                                <Select
                                    value={form.competencia_id}
                                    onValueChange={(v) => handleChange('competencia_id', v)}
                                >
                                    <SelectTrigger data-testid="select-competencia">
                                        <SelectValue placeholder="Seleccionar competencia (opcional)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sin competencia asociada</SelectItem>
                                        {competencias.map(c => (
                                            <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Malla Curricular y opciones */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Malla Curricular</Label>
                                <div className="flex gap-4 pt-2">
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="malla-2024"
                                            checked={form.malla_curricular.includes('2024')}
                                            onCheckedChange={(checked) => {
                                                const m = form.malla_curricular.filter(v => v !== '2024');
                                                if (checked) m.push('2024');
                                                handleChange('malla_curricular', m);
                                            }}
                                        />
                                        <label htmlFor="malla-2024" className="text-sm cursor-pointer">2024</label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="malla-2025"
                                            checked={form.malla_curricular.includes('2025')}
                                            onCheckedChange={(checked) => {
                                                const m = form.malla_curricular.filter(v => v !== '2025');
                                                if (checked) m.push('2025');
                                                handleChange('malla_curricular', m);
                                            }}
                                        />
                                        <label htmlFor="malla-2025" className="text-sm cursor-pointer">2025</label>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-end space-x-2 pb-1">
                                <Checkbox
                                    id="es-aula-virtual"
                                    checked={form.es_aula_virtual}
                                    onCheckedChange={(checked) => handleChange('es_aula_virtual', !!checked)}
                                />
                                <label htmlFor="es-aula-virtual" className="text-sm cursor-pointer">Aula Virtual</label>
                            </div>
                            <div className="flex items-end space-x-2 pb-1">
                                <Checkbox
                                    id="es-complementario"
                                    checked={form.es_complementario}
                                    onCheckedChange={(checked) => handleChange('es_complementario', !!checked)}
                                />
                                <label htmlFor="es-complementario" className="text-sm cursor-pointer">Curso Complementario</label>
                            </div>
                        </div>

                        {/* Vincular con curso de malla */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                Vincular con Malla Curricular
                            </Label>
                            <p className="text-xs text-muted-foreground">Al vincular, los bomberos inscritos en esta capacitación se reflejarán en su malla</p>
                            <Select
                                value={form.curso_malla_id}
                                onValueChange={(v) => handleChange('curso_malla_id', v)}
                            >
                                <SelectTrigger data-testid="select-curso-malla">
                                    <SelectValue placeholder="Sin vincular" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Sin vincular</SelectItem>
                                    {['2024', '2025'].map(yr => {
                                        const cursosAnio = cursosMalla.filter(c => c.anio === yr);
                                        if (cursosAnio.length === 0) return null;
                                        return cursosAnio.map(c => (
                                            <SelectItem key={c.id} value={c.id}>
                                                [{yr}] {c.nivel?.charAt(0).toUpperCase() + c.nivel?.slice(1)} — {c.nombre}
                                            </SelectItem>
                                        ));
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Programación */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            Programación
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="fecha_inicio">Fecha de Inicio *</Label>
                                <Input
                                    id="fecha_inicio"
                                    type="date"
                                    value={form.fecha_inicio}
                                    onChange={(e) => handleChange('fecha_inicio', e.target.value)}
                                    required
                                    data-testid="input-fecha-inicio"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="fecha_fin">Fecha de Término *</Label>
                                <Input
                                    id="fecha_fin"
                                    type="date"
                                    value={form.fecha_fin}
                                    onChange={(e) => handleChange('fecha_fin', e.target.value)}
                                    required
                                    data-testid="input-fecha-fin"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="horas_duracion">Horas de Duración</Label>
                                <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="horas_duracion"
                                        type="number"
                                        min="1"
                                        max="500"
                                        value={form.horas_duracion}
                                        onChange={(e) => handleChange('horas_duracion', parseInt(e.target.value) || 1)}
                                        data-testid="input-horas"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="instructor">Instructor</Label>
                                <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="instructor"
                                        value={form.instructor}
                                        onChange={(e) => handleChange('instructor', e.target.value)}
                                        placeholder="Nombre del instructor"
                                        className="flex-1"
                                        data-testid="input-instructor"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="lugar">Lugar</Label>
                                <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="lugar"
                                        value={form.lugar}
                                        onChange={(e) => handleChange('lugar', e.target.value)}
                                        placeholder="Ubicación de la capacitación"
                                        className="flex-1"
                                        data-testid="input-lugar"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="cupo_maximo">Cupo Máximo</Label>
                                <Input
                                    id="cupo_maximo"
                                    type="number"
                                    min="1"
                                    max="500"
                                    value={form.cupo_maximo}
                                    onChange={(e) => handleChange('cupo_maximo', parseInt(e.target.value) || 1)}
                                    data-testid="input-cupo"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Estado</Label>
                                <Select
                                    value={form.estado}
                                    onValueChange={(v) => handleChange('estado', v)}
                                >
                                    <SelectTrigger data-testid="select-estado">
                                        <SelectValue placeholder="Estado" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="programada">Programada</SelectItem>
                                        <SelectItem value="en_curso">En Curso</SelectItem>
                                        <SelectItem value="finalizada">Finalizada</SelectItem>
                                        <SelectItem value="cancelada">Cancelada</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Contenido Académico */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle>Contenido Académico</CardTitle>
                        <CardDescription>Información detallada del programa de capacitación</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="objetivos">Objetivos de Aprendizaje</Label>
                            <Textarea
                                id="objetivos"
                                value={form.objetivos}
                                onChange={(e) => handleChange('objetivos', e.target.value)}
                                placeholder="Objetivos que se esperan alcanzar..."
                                rows={3}
                                data-testid="input-objetivos"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="contenidos">Contenidos / Temario</Label>
                            <Textarea
                                id="contenidos"
                                value={form.contenidos}
                                onChange={(e) => handleChange('contenidos', e.target.value)}
                                placeholder="Lista de temas a cubrir..."
                                rows={4}
                                data-testid="input-contenidos"
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="requisitos_previos">Requisitos Previos</Label>
                                <Textarea
                                    id="requisitos_previos"
                                    value={form.requisitos_previos}
                                    onChange={(e) => handleChange('requisitos_previos', e.target.value)}
                                    placeholder="Competencias o cursos previos necesarios..."
                                    rows={2}
                                    data-testid="input-requisitos"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="materiales_necesarios">Materiales Necesarios</Label>
                                <Textarea
                                    id="materiales_necesarios"
                                    value={form.materiales_necesarios}
                                    onChange={(e) => handleChange('materiales_necesarios', e.target.value)}
                                    placeholder="EPP, equipos, materiales..."
                                    rows={2}
                                    data-testid="input-materiales"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-end gap-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate('/bomberos/capacitaciones')}
                    >
                        Cancelar
                    </Button>
                    <Button type="submit" disabled={saving} data-testid="save-btn">
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Capacitación')}
                    </Button>
                </div>
            </form>
        </div>
        </>
    );
}
